import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useGenerationPolling } from '@/hooks/useGenerationPolling';
import { PageHeader } from '@/components/layout/PageHeader';
import { MarketAnalysisModal, FormData as ModalFormData } from '@/components/market-analysis/MarketAnalysisModal';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { SubscriptionLimitBanner } from '@/components/shared/SubscriptionLimitBanner';
import { AiItemsList } from '@/components/shared/AiItemsList';
import { AiDetailPanel } from '@/components/shared/AiDetailPanel';
import { useAiPagePermissions } from '@/hooks/useAiPagePermissions';
import { projectsApi, marketAnalysesApi } from '@/utils/api-pb';
import type { Project, MarketAnalysis } from '@/utils/pocketbase';

import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/i18n/LanguageContext';
const FREE_ANALYSIS_LIMIT = 1;

export default function MarketAnalysisPage() {
  const { t } = useTranslation('market-analysis');
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Data state
  const [projects, setProjects] = useState<Project[]>([]);
  const [analyses, setAnalyses] = useState<MarketAnalysis[]>([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState<MarketAnalysis | null>(null);

  // Form state
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [analysisToDelete, setAnalysisToDelete] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    project_id: '',
    keywords: '',
    competitors: '',
    advantage: '',
    comment: ''
  });

  // Permissions
  const { isFree, canCreateNew, canEdit, canDelete } = useAiPagePermissions(analyses.length, FREE_ANALYSIS_LIMIT);

  // Polling for async generation
  const handleGenerationCompleted = useCallback(() => {
    setIsLoading(false);
    setShowEditModal(false);
    setShowNewModal(false);
    setFormData({ project_id: '', keywords: '', competitors: '', advantage: '', comment: '' });
    loadData();
    toast.success(t('toast.ready'));
  }, []);

  const { startPolling } = useGenerationPolling({
    collection: 'market_analyses',
    onCompleted: handleGenerationCompleted,
    onFailed: () => {
      setIsLoading(false);
      toast.error(t('toast.generateError'));
    },
    onTimeout: () => {
      setIsLoading(false);
      toast.error(t('toast.timeoutError'));
    },
  });

  // Tooltip state for create button
  const [tooltipVisible, setTooltipVisible] = useState<{ id: string, type: string } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  // Auto-select analysis (prefer recordId from query param, fallback to first)
  useEffect(() => {
    if (analyses.length > 0 && !selectedAnalysis) {
      const targetId = searchParams.get('recordId');
      const target = targetId ? analyses.find(a => a.id === targetId) : null;
      setSelectedAnalysis(target ?? analyses[0] ?? null);
    }
  }, [analyses, selectedAnalysis, searchParams]);

  const loadData = async () => {
    try {
      setIsInitialLoading(true);
      const [projectsResult, analysesResult] = await Promise.all([
        projectsApi.list(),
        marketAnalysesApi.list(),
      ]);
      setProjects(projectsResult);
      setAnalyses(analysesResult);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handleSubmitModal = async (modalFormData: ModalFormData) => {
    setIsLoading(true);

    try {
      const project = projects.find(p => p.id === modalFormData.project_id);
      const productDescription = project?.description || project?.name || '';

      const result = await marketAnalysesApi.generate(modalFormData.project_id, {
        productDescription,
        keywords: modalFormData.keywords,
        competitors: modalFormData.competitors,
        advantage: modalFormData.advantage,
        comment: modalFormData.comment,
        lang,
        replaceId: isEditing ? selectedAnalysis?.id : undefined,
      });

      if (result.pending && result.analysis?.id) {
        // Backend returned 202: poll for completion
        startPolling(result.analysis.id);
        return;
      }

      // Non-pending response (direct completion)
      setShowEditModal(false);
      setShowNewModal(false);
      setFormData({ project_id: '', keywords: '', competitors: '', advantage: '', comment: '' });
      setIsLoading(false);
      loadData();
    } catch (error) {
      console.error('Error creating/updating analysis:', error);
      const errorMessage = error instanceof Error ? error.message : t('toast.createError');
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await marketAnalysesApi.delete(id);
      setShowDeleteConfirm(false);
      setAnalysisToDelete(null);
      if (selectedAnalysis?.id === id) {
        setSelectedAnalysis(null);
      }
      loadData();
    } catch (error) {
      console.error('Error deleting analysis:', error);
      toast.error(t('toast.deleteError'));
    }
  };

  const handleCreateNew = () => {
    if (!canCreateNew) return;

    setFormData({
      project_id: '',
      keywords: '',
      competitors: '',
      advantage: '',
      comment: ''
    });
    setIsEditing(false);
    setSelectedAnalysis(null);
    setShowNewModal(true);
  };

  const handleEditAnalysis = (analysis: MarketAnalysis) => {
    if (!canEdit) return;

    setFormData({
      project_id: analysis.project || '',
      keywords: analysis.keywords || '',
      competitors: analysis.competitors || '',
      advantage: analysis.advantage || '',
      comment: analysis.comment || ''
    });
    setSelectedAnalysis(analysis);
    setIsEditing(true);
    setShowEditModal(true);
  };

  const handleSelectAnalysis = (analysis: MarketAnalysis) => {
    setSelectedAnalysis(analysis);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setShowNewModal(false);
    setIsEditing(false);
  };

  const handleConfirmExit = () => {
    setShowExitConfirm(false);
    setShowEditModal(false);
    setShowNewModal(false);
    setIsEditing(false);
  };

  // Get project name for analysis
  const getProjectName = (analysis: MarketAnalysis) => {
    const project = projects.find(p => p.id === analysis.project);
    return project?.name || analysis.product_description?.substring(0, 30) || t('noTitle');
  };

  // Count analyses per project for numbering
  const analysisNumbers = useMemo(() => {
    const projectCounts: Record<string, number> = {};
    const numbers: Record<string, { displayNumber: number; total: number }> = {};

    analyses.forEach(analysis => {
      const projectId = analysis.project || '';
      projectCounts[projectId] = (projectCounts[projectId] || 0) + 1;
    });

    const projectIndexes: Record<string, number> = {};
    analyses.forEach(analysis => {
      const projectId = analysis.project || '';
      projectIndexes[projectId] = (projectIndexes[projectId] || 0) + 1;
      numbers[analysis.id] = {
        displayNumber: projectIndexes[projectId] ?? 1,
        total: projectCounts[projectId] ?? 1
      };
    });

    return numbers;
  }, [analyses]);

  const getAnalysisTitle = (analysis: MarketAnalysis) => {
    const numberInfo = analysisNumbers[analysis.id];
    const shouldShowNumber = numberInfo && numberInfo.total > 1;
    const displayNum = numberInfo?.displayNumber ?? 1;
    return t('itemTitle', { projectName: getProjectName(analysis) }) + (shouldShowNumber ? ` ${displayNum}` : '');
  };

  return (
    <>{isInitialLoading ? (
          /* Initial Loading State */
          <>
            <PageHeader title={t('title')} description={t('description')} />
            <div className="flex items-center justify-center py-[80px]">
              <div className="w-[32px] h-[32px] border-2 border-[#2197f1] border-t-transparent rounded-full animate-spin" />
            </div>
          </>
        ) : analyses.length === 0 ? (
          /* Empty State */
          <><PageHeader title={t('title')} description={t('description')} />
          <div className="flex flex-col gap-[24px]">
            <div className="font-segoe text-[16px] leading-[24px] text-[#565858] max-w-[769px]">
              <p>
                {t('empty.text')}
              </p>
            </div>

            <button
              onClick={handleCreateNew}
              disabled={!canCreateNew}
              onMouseEnter={() => !canCreateNew && setTooltipVisible({ id: 'create-new', type: 'create' })}
              onMouseLeave={() => setTooltipVisible(null)}
              className="relative inline-block"
            >
              <div className={`${canCreateNew
                ? 'bg-[#2197f1] hover:bg-[#1a7bc7]'
                : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed opacity-60'
                } rounded-[16px] px-[24px] py-[12px] transition-colors inline-block`}>
                <p className="font-segoe font-semibold text-[16px] leading-[24px] text-white text-center">
                  {t('newButton')}
                </p>
              </div>

              {tooltipVisible?.id === 'create-new' && !canCreateNew && (
                <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                  <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                    {t('tooltip.freeLimitCreate')}
                  </p>
                </div>
              )}
            </button>
          </div>
          </>
        ) : (
          /* List with Selected Analysis */
          <div className="flex flex-col lg:flex-row gap-[24px]">
            {/* Left Sidebar - Analyses List */}
            <div className="flex flex-col gap-[8px] w-full lg:w-[424px] lg:flex-shrink-0 lg:sticky lg:top-[24px] lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto">
              <PageHeader title={t('title')} description={t('description')} />
              <AiItemsList
                items={analyses}
                selectedId={selectedAnalysis?.id ?? null}
                getItemTitle={getAnalysisTitle}
                onSelect={handleSelectAnalysis}
                isItemDisabled={(_item, index) => isFree && index > 0}
                tooltipMessage={t('tooltip.paidOnly')}
              />

              {/* Create New Button */}
              <button
                onClick={handleCreateNew}
                disabled={!canCreateNew}
                onMouseEnter={() => !canCreateNew && setTooltipVisible({ id: 'create-new-sidebar', type: 'create' })}
                onMouseLeave={() => setTooltipVisible(null)}
                className="relative"
              >
                <div className={`${canCreateNew
                  ? 'bg-[#2197f1] hover:bg-[#1a7bc7]'
                  : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed opacity-60'
                  } rounded-[16px] px-[24px] py-[12px] transition-colors`}>
                  <p className="font-segoe font-semibold text-[16px] leading-[24px] text-white text-center">
                    {t('newButton')}
                  </p>
                </div>

                {tooltipVisible?.id === 'create-new-sidebar' && !canCreateNew && (
                  <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                    <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                      {t('tooltip.freeLimitCreate')}
                    </p>
                  </div>
                )}
              </button>

              {/* Limit Warning for Free Tier */}
              {isFree && analyses.length >= 1 && (
                <SubscriptionLimitBanner />
              )}
            </div>

            {/* Right Content - Selected Analysis */}
            {selectedAnalysis && (
              <AiDetailPanel
                key={selectedAnalysis.id}
                title={t('itemTitle', { projectName: getProjectName(selectedAnalysis) })}
                content={selectedAnalysis.generated_text}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={() => handleEditAnalysis(selectedAnalysis)}
                onDelete={() => {
                  setAnalysisToDelete(selectedAnalysis.id);
                  setShowDeleteConfirm(true);
                }}
                editButtonText={t('detail.editButton')}
                actionButtons={[
                  { text: t('detail.actionPersona'), onClick: () => navigate('/personas') }
                ]}
                pdfExportData={selectedAnalysis.generated_text ? {
                  title: t('itemTitle', { projectName: getProjectName(selectedAnalysis) }),
                  content: selectedAnalysis.generated_text,
                  artifactType: 'market_analysis',
                  artifactTypeLabel: t('detail.pdfLabel'),
                  projectName: getProjectName(selectedAnalysis),
                  date: selectedAnalysis.created,
                } : undefined}
                tooltipEditText={t('tooltip.paidOnly')}
                tooltipDeleteText={t('tooltip.paidOnly')}
                emptyText={t('detail.emptyText')}
              />
            )}
          </div>
        )}

      {/* Exit Confirmation Modal */}
      <ConfirmModal
        isOpen={showExitConfirm}
        onClose={() => setShowExitConfirm(false)}
        onConfirm={handleConfirmExit}
        title={t('confirm.exitTitle')}
        description={t('confirm.exitDescription')}
        confirmText={t('confirm.exitConfirm')}
        cancelText={t('confirm.exitCancel')}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setAnalysisToDelete(null);
        }}
        onConfirm={() => analysisToDelete && handleDelete(analysisToDelete)}
        title={t('confirm.deleteTitle')}
        description={t('confirm.deleteDescription')}
        confirmText={t('confirm.deleteConfirm')}
        cancelText={t('confirm.exitCancel')}
      />

      {/* Market Analysis Modal (Create/Edit) */}
      <MarketAnalysisModal
        isOpen={showNewModal || showEditModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmitModal}
        projects={projects}
        initialData={formData}
        isEditing={isEditing}
        isLoading={isLoading}
      />
    </>
  );
}
